import { type DocNode } from '@atlaskit/adf-schema';
import { type LabelStack } from '@atlaskit/react-ufo/interaction-context';
import type { AgentCreatorType } from '@atlaskit/rovo-agent-components/common/types';
import type {
	AiFeatureRequestEnvelopeRovoConvoStarterRecommendationRequestNew,
	AIFeatureResponseEnvelopeRovoConvoStarterRecommendationResponseNew,
} from '@atlassian/convo-ai-api';

import { ASSISTANCE_SERVICE_ERROR_CODES } from '../../common/constants/public';
import { isApiError } from '../../common/utils/api-error';

import { type AgentAction } from './agent-actions/types';
import {
	type BulkMessageActionPayload,
	type BulkMessageActionResponse,
	type MessageAction,
	type MessageActionPayload,
	type MessageActionPayloadContext,
	type MessageActionResponse,
} from './message-actions/types';
import { type PluginInvocationMessage } from './plugins';

/**
 * Represents a parsed SSE message with metadata from the standard W3C SSE format.
 * When includeMetadata is true, readSSEStream yields this type.
 * - eventType: from SSE `event:` line
 * - id: from SSE `id:` line (used e.g. for Last-Event-ID / reconnect).
 * - data: parsed JSON from SSE `data:` line.
 * The __isSSEWrapper discriminator ensures this type is never confused with StreamMessage.
 */
export type ParsedSSEMessage<T> = {
	/** Discriminator field - guarantees this is an SSE wrapper, not a StreamMessage */
	__isSSEWrapper: true;
	eventType?: string;
	data: T;
	id?: string;
};

/**
 * Type guard to check if a message is a ParsedSSEMessage wrapper.
 * Uses the __isSSEWrapper discriminator to avoid false positives with StreamMessage.
 */
export const isSSEMessage = <T>(message: unknown): message is ParsedSSEMessage<T> => {
	return (
		message !== null &&
		typeof message === 'object' &&
		'__isSSEWrapper' in message &&
		(message as ParsedSSEMessage<T>).__isSSEWrapper === true
	);
};

export const CONVERSATION_CHANNEL_NOT_FOUND_ERROR = 'convoai.rest.not.found' as const;

export const ACCEPTABLE_USE_VIOLATIONS_ERROR = 'ACCEPTABLE_USE_VIOLATIONS' as const;

export type MessageFileId = {
	id: string;
	name: string;
};

export type WebSearchConfigurationResponse = {
	resourceAri: string;
	enabled: boolean;
};

export type RovoUserSettings = {
	/**
	 * Whether the user allows Rovo to save memories from conversations
	 */
	allowMemory: boolean;
	/**
	 * Whether the user allows Rovo to reference past conversations
	 */
	allowConversationReference: boolean;
	/**
	 * Whether the user has opted in to having their chat messages stored.
	 * Omitted by the backend (undefined) when the user is not eligible for this preference.
	 */
	storeUserMessages?: boolean;
	/**
	 * Whether the user is eligible to set storeUserMessages.
	 * BE-computed via the `ai_mate_show_store_messages` feature gate.
	 */
	storeUserMessagesEligible?: boolean;
};

/**
 * Represents a memory reference (can be AGENT or CONVERSATION)
 */
export type RovoMemoryReference = {
	referenceType: 'AGENT' | 'CONVERSATION';
	referenceId: string;
};

/**
 * Represents a memory in Rovo
 */
export type RovoMemory = {
	/**
	 * Unique identifier for the memory
	 */
	id: string;
	/**
	 * The channel ID associated with the memory
	 */
	channelId: string;
	/**
	 * The user ID who created the memory
	 */
	userId: string;
	/**
	 * Reference to the agent or conversation
	 */
	reference: RovoMemoryReference;
	/**
	 * The content of the memory
	 */
	content: string;
	/**
	 * When the memory was created
	 */
	createdAt: string;
	/**
	 * When the memory expires
	 */
	expiresAt: string;
};

/**
 * Response from the get memories API
 */
export type RovoMemoriesResponse = RovoMemory[];

/**
 * Request body for the POST /memories/memory-edits API.
 */
export type MemoryEditRequest = {
	content: string;
};

/**
 * Request body for the POST /memories/create API.
 */
export type CreateMemoryRequest = {
	content: string;
};

/**
 * Response from the POST /memories/create API.
 */
export type CreateMemoryResponse = {
	memory: {
		id: string;
		conversationId: string;
		agentId: string;
		content: string;
		createdAt: string;
		expiresAt: string | null;
	};
};

/**
 * Response from the POST /memories/memory-edits API.
 */
export type MemoryEditPromptResponse = {
	id: string;
	content: string;
	createdAt: string;
	metadata?: Record<string, string>;
};

/**
 * Response from the GET /memories/blurb API.
 * Contains a human-readable narrative summary of the user's implicit identity
 * (role, OKRs, projects, tools) built from TWG activity and conversational memory.
 * `content` is null when there is insufficient signal to generate one.
 */
export type RovoMemoryBlurbResponse = {
	content: string | null;
};

/**
 * A single pending memory edit suggested by the user.
 */
export type RovoMemoryEdit = {
	/**
	 * Unique identifier for the memory edit
	 */
	id: string;
	/**
	 * The suggested edit content
	 */
	content: string;
	/**
	 * When the edit was created
	 */
	createdAt: string;
	/**
	 * Optional metadata associated with the edit
	 */
	metadata: Record<string, unknown> | null;
};

/**
 * Response from the GET /memories/memory-edits API.
 * Paginated list of pending memory edits.
 */
export type RovoMemoryEditsResponse = {
	items: RovoMemoryEdit[];
	nextPageKey: string | null;
};

/**
 * Response from the GET /memories/memory-edits/count API.
 */
export type RovoMemoryEditsCountResponse = {
	count: number;
};

export enum PromptCategory {
	ANALYZE = 'Analyze',
	BRAINSTORM = 'Brainstorm',
	REVIEW = 'Review',
	SUMMARIZE = 'Summarize',
	CREATE = 'Create',
	EXECUTE = 'Execute',
	FIND = 'Find',
	LEARN = 'Learn',
}

export enum PromptRole {
	GENERAL = 'General',
	ENGINEERING = 'Engineering',
	PRODUCT = 'Product',
	PROGRAM_MANAGEMENT = 'Program Management',
	DESIGN = 'Design',
	MARKETING = 'Marketing',
	IT_OPS = 'IT Ops',
	SALES = 'Sales',
	CUSTOMER_SUPPORT = 'Customer Support',
	HELPDESK_SUPPORT = 'Helpdesk Support',
	SECURITY = 'Security',
}

export type SimpleUserProfile = {
	id: string;
	ari: string;
	name: string;
	picture?: string;
};

export type GetPromptResponse = {
	id: string;
	prompt: string;
	title: string;
	useCase?: PromptCategory;
	roles?: PromptRole[];
	creator: SimpleUserProfile;
	sharedWith?: SimpleUserProfile[];
	isFavorited: boolean;
	appIds?: string[];
	isSystemPrompt?: boolean;
	isPrivate?: boolean;
	inactive?: boolean;
	favoriteCount?: number;
	createdAt?: number;
	inLibrary?: boolean;
};

export type GetAllPromptsResponse = {
	prompts: GetPromptResponse[];
};

export type CreatePromptRequest = {
	title: string;
	prompt: string;
	useCase?: PromptCategory;
	roles?: PromptRole[];
	appIds?: string[];
};

export type UpdatePromptRequest = {
	title?: string;
	prompt?: string;
	useCase?: PromptCategory;
	roles?: PromptRole[];
	appIds?: string[];
	isPrivate?: boolean;
	isFavorited?: boolean;
};

export type AddPromptsToLibraryRequest = {
	shareIds: string[];
};

export type FavoritePromptItem = {
	shareId: string;
	isFavorite: boolean;
};

export type FavoritePromptsRequest = {
	prompts: FavoritePromptItem[];
};

export type BulkUpdatePrivacyItem = {
	shareId: string;
	isPrivate: boolean;
};

export type BulkUpdatePrivacyRequest = {
	prompts: BulkUpdatePrivacyItem[];
};

export type BulkUpdatePrivacyResult = {
	shareId: string;
	success: boolean;
	isPrivate: boolean | null;
	error: string | null;
};

export type BulkUpdatePrivacyResponse = {
	results: BulkUpdatePrivacyResult[];
};

export type BulkGetPromptsRequest = {
	shareIds: string[];
};

export type BulkGetPromptResult = {
	shareId: string;
	success: boolean;
	prompt: GetPromptResponse | null;
	error: string | null;
};

export type BulkGetPromptsResponse = {
	results: BulkGetPromptResult[];
};

export type ConversationContent = {
	id: string;
	content_display_name: string;
	content_ari: string;
	created_at: string;
	action_conversation_id: string;
	invocation_id: string;
	action_status: string;
	action_key: string;
	owner: string;
};

export type HumanMessage = {
	role: 'HUMAN';
	content: string | DocNode;
	id: string;
	time_created: string;
	user_ari: string;
	plugin_invocations: PluginInvocationMessage[];
	isFromSeededConversation?: boolean;
	requestId?: string;
	file_uploads?: MessageFileId[];
	parent_message_id?: string | null; // null for legacy
};

export type AssistantMessage = Message & {
	role: 'ASSISTANT';
};

export type ContentType = 'slide' | 'live doc' | 'whiteboard' | 'database';

export const SUGGESTED_PROMPT_TILE_STREAM_MESSAGE_TYPE = 'PROMPT_TILE';

export type SuggestedPromptTile = {
	title: string;
	text: string;
	content_type: ContentType;
	icon: string;
};

export type SuggestedPromptTileStreamMessage = {
	type: typeof SUGGESTED_PROMPT_TILE_STREAM_MESSAGE_TYPE;
	message: SuggestedPromptTile;
};

export type SuggestedPromptsResponse = {
	ai_feature_output: {
		prompt_tiles: SuggestedPromptTile[];
	};
};

// ************************************************************************************************
// Response data & metadata
// Sent by POST:/chat endpoint
// ************************************************************************************************

export type ModelUsage = {
	total_tokens: number;
	prompt_tokens: number;
	completion_tokens: number;
	request_count: number;
	total_cost: number;
	duration: number;
};

export type Usage = {
	model_usage: {
		[key: string]: ModelUsage;
	};
	total: ModelUsage;
};

export type Durations = {
	events: {
		[key: string]: number;
	};
	total: number;
};

export type DocSegment = {
	content: string;
	token_count: number;
	length: number;
	parent_ari: string;
	similarity_score: number;
};

export type DocSegments = Array<DocSegment>;

export type AgentResponse = {
	message: AssistantMessage;
	human_message?: HumanMessage;
	usage_data?: Usage;
	durations_data?: Durations;
};

// ************************************************************************************************
//  Streaming Messages
// Sent by POST:/chat/stream endpoint
// ************************************************************************************************

export type StreamResponse = {
	type: 'FINAL_RESPONSE';
	message: AgentResponse;
	millisOffset?: number;
	metadata?: {
		request_id?: string;
	};
	streamingComplete?: boolean;
};

export type StreamError = {
	type: 'ERROR';
	message: {
		content: string;
		status_code?: number;
		message_template?: string;
	};
	millisOffset: number;
	metadata: null | {
		error_message?: string;
		request_id?: string;
		timeout?: number;
	};
};

export type StreamReconnectSupportPart = {
	type: 'RECONNECT_SUPPORTED';
};

export type StreamHeartbeat = {
	type: 'HEART_BEAT';
	content: string;
};

export type StreamAnswerPart = {
	type: 'ANSWER_PART';
	message: { content: string; role: 'ASSISTANT' };
	millisOffset: number;
	metadata?: {
		run_id?: string;
		request_id?: string;
	};
};

export type StreamDynamicLayoutSubAgent = {
	type: 'DL_SUB_AGENT';
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	message: any;
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamReasoningPart = {
	type: 'REASONING_PART';
	message: { content: string; role: 'ASSISTANT'; stepId: string };
	millisOffset: number;
	metadata?: {
		request_id?: string;
	};
};

export type StreamTrace = {
	type: 'TRACE';
	message: { content: string; message_template?: string };
	millisOffset: number;
	metadata?: {
		run_id: string;
		request_id: string;
		plugin_name?: string;
		plugin_input?: string;
	};
};

export type StreamMemoryUpdateTrace = {
	type: 'TRACE';
	message: { content: string; message_template: 'MEMORY_UPDATE' };
	millisOffset: number;
	metadata?: {
		request_id: string;
		plugin_name?: string;
		plugin_input?: string;
	};
};

export type StreamResearchTrace = {
	type: 'TRACE';
	message: { message_template: 'RESEARCH'; content: string };
	millisOffset: number;
	metadata?: { request_id: string };
};

export type AnalysingQueryTrace = {
	type: 'TRACE';
	message: { message_template: 'ANALYSING_QUERY'; content: string };
	millisOffset: number;
	metadata?: { request_id: string };
};

export type WritingTrace = {
	type: 'TRACE';
	message: { message_template: 'WRITING'; content: string; user_query?: string | null };
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamResearchStepContent = {
	type: 'TRACE';
	message: { message_template: 'RESEARCH_STEP'; content: string; stepId: string; roundId: number };
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamResearchStepTitle = {
	type: 'TRACE';
	message: {
		message_template: 'RESEARCH_STEP_TITLE';
		title: string;
		stepId: string;
		roundId: number;
		toolIcon?: string;
		toolName?: string;
	};
};

export type StreamResearchPlan = {
	type: 'TRACE';
	message: { message_template: 'RESEARCH_PLAN'; content: string; roundId: number };
	millisOffset: number;
	metadata?: { request_id: string };
};

export type TodoItem = {
	id: string;
	content: string;
	status: string;
	priority: string;
	active_form: string;
};

export type StreamTodoSnapshot = {
	type: 'TODO_SNAPSHOT';
	message: { todos: TodoItem[] };
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamTaskStatus = {
	type: 'TASK_STATUS';
	message: {
		task_id: string;
		title: string;
		status: ApiTaskStatus;
		message_id: string | null;
		completion_message_id: string | null;
		created_at: string;
		started_at: string | null;
		updated_at: string;
		completed_at: string | null;
		current_message_id: string | null;
		execution_segments: Record<string, string> | null;
	};
	millisOffset: number;
	metadata?: { request_id: string | null };
	sequence_id?: string;
};

export type StreamResearchStepTitleShort = {
	type: 'TRACE';
	message: {
		message_template: 'RESEARCH_STEP_TITLE_SHORT';
		title: string;
		stepId: string;
		roundId: number;
		totalRoundId?: number | null;
		formulated_query?: string | null;
		sources?: Sources | null;
		toolIcon?: string;
		toolName?: string;
	};
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamResearchStepSource = {
	type: 'TRACE';
	message: {
		message_template: 'RESEARCH_STEP_SOURCE';
		sources: Source[];
		stepId: string;
		roundId: number;
	};
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamResearchStepCompleted = {
	type: 'TRACE';
	message: {
		message_template: 'RESEARCH_STEP_COMPLETE';
		stepId: string;
		roundId: number;
	};
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamResearchStepIcons = {
	type: 'TRACE';
	message: {
		message_template: 'RESEARCH_STEP_ICONS';
		content: string;
		products: string[];
		stepId: string;
		roundId: number;
	};
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamResearchStepDocument = {
	type: 'TRACE';
	message: {
		message_template: 'RESEARCH_STEP_DOCUMENT';
		content: string;
		title: string;
		stepId: string;
		roundId: number;
		product: string;
		url: string;
		toolName: string;
	};
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamResearchStart = {
	type: 'TRACE';
	message: {
		message_template: 'RESEARCH_START';
		totalRoundId: number;
	};
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamSkillStart = {
	type: 'TRACE';
	message: {
		message_template: 'SKILL_START';
		skill_status_metadata: { aliases: string[] };
	};
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamSkillEnd = {
	type: 'TRACE';
	message: {
		message_template: 'SKILL_END';
		skill_status_metadata: { aliases: string[] };
	};
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamFastModeResponseAvailable = {
	type: 'TRACE';
	message: {
		message_template: 'FAST_MODE_RESPONSE_AVAILABLE';
		content: string;
	};
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamFastModeEnabled = {
	type: 'TRACE';
	message: {
		message_template: 'FAST_MODE_ENABLED';
		content: string;
	};
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamRemoveAnswerNow = {
	type: 'TRACE';
	message: {
		message_template: 'REMOVE_ANSWER_NOW';
		content: string;
	};
	millisOffset: number;
	metadata?: { request_id: string };
};

export type FollowUpObject = {
	type: 'SYNTHETIC' | 'LLM_GENERATED';
	text: string;
};

export type StreamFollowUp = {
	type: 'FOLLOW_UP_QUERIES';
	message: {
		follow_ups: FollowUpObject[] | null;
	};
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamAgenticUi = {
	type?: 'AGENTIC_UI';
	message: {
		content: unknown[];
	};
	millisOffset?: number;
	metadata?: {
		request_id?: string;
	};
};

export type StreamActionPart = {
	type: 'ACTION_PART';
	message: MessageAction;
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamActionClassification = {
	type: 'ACTION_CLASSIFICATION';
	message: {
		action_key: string;
		content: string;
		metadata?: {
			promptVersion?: string;
			contentId?: string;
		};
	};
	millisOffset: number;
};

export type StreamActionComplete = {
	type: 'ACTION_COMPLETE';
	message: MessageAction;
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamActionPrepare = Omit<StreamActionPart, 'type'> & {
	type: 'ACTION_PREPARE';
};

export type StreamPromptRedaction = {
	type: 'PROMPT_REDACTION';
	message: {
		original_prompt: string;
		redacted_prompt: string;
	};
	millisOffset: number;
	metadata: { request_id: string | null };
};

export type StreamPromptBlock = {
	type: 'PROMPT_BLOCKED';
	message: {
		message_template: string;
		content: string;
		status_code: number;
	};
	millisOffset: number;
	metadata: { error_message: string; request_id: string | null };
};

export type StreamResponseRedaction = {
	type: 'RESPONSE_REDACTION';
	message: {
		original_response: string;
		redacted_response: string;
	};
	millisOffset: number;
	metadata: { request_id: string | null } | null;
};

export type StreamResponseBlock = {
	type: 'RESPONSE_BLOCKED';
	message: {
		message_template: string;
		content: string;
		status_code: number;
	};
	millisOffset: number;
	metadata: { error_message: string; request_id: string | null };
};

export type StreamAttachmentBlock = {
	type: 'ATTACHMENT_BLOCKED';
	message: {
		message_template: string;
		content: string;
		status_code: number;
	};
	millisOffset: number;
	metadata: { error_message: string; request_id: string | null };
};

export type AnswerGenerationType = 'LUMINA' | 'STANDARD';
export type SteamAnsGenerationType = {
	type: 'ANSWER_GENERATION_TYPE';
	message: {
		answer_generation_type: AnswerGenerationType;
	};
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamMessage =
	| StreamTrace
	| StreamResearchTrace
	| StreamResearchStepContent
	| StreamResearchStepTitle
	| StreamResearchStepTitleShort
	| StreamResearchStepSource
	| StreamResearchStepCompleted
	| StreamResearchStepIcons
	| StreamResearchStepDocument
	| StreamResearchPlan
	| AnalysingQueryTrace
	| WritingTrace
	| StreamAnswerPart
	| StreamReasoningPart
	| StreamResponse
	| StreamError
	| StreamFollowUp
	| StreamActionPart
	| StreamActionPrepare
	| StreamActionComplete
	| StreamActionClassification
	| ConversationChannelDataMessage
	| ScopeDeterminedMessage
	| StreamReconnectSupportPart
	| SteamAnsGenerationType
	| StreamDynamicLayoutSubAgent
	| StreamHeartbeat
	| StreamMemoryUpdateTrace
	| StreamTodoSnapshot
	| StreamTaskStatus
	| StreamPromptRedaction
	| StreamPromptBlock
	| StreamResponseRedaction
	| StreamResponseBlock
	| StreamAttachmentBlock;

/*
NLCreate Stream message types
*/
export type NLCreateAgentPayload = {
	latest_user_message: string;
	draft_agent: {
		conversation_starters: string[];
		description: string;
		instructions: string;
		name: string;
	};
	conversation_history: {
		content: string;
		role: 'HUMAN' | 'ASSISTANT';
	}[];
	mime_type: 'text/adf';
};

export type NLCreateAgentStreamDraftAgent = {
	type: 'DRAFT_AGENT';
	message: {
		name: string;
		description: string;
		instructions: string;
		conversation_starters: string[];
	};
	metadata: {
		request_id: string;
	};
};
export type NLCreateAgentStreamAnswerPart = {
	type: 'ANSWER_PART';
	message: {
		content: string;
	};
	metadata: {
		request_id: string;
	};
};

export type NLCreateAgentStreamFinalResponse = {
	type: 'FINAL_RESPONSE';
	message: {
		content: string;
	};
	metadata: {
		request_id: string;
	};
};

export type NLCreateAgentStreamError = {
	type: 'ERROR';
	message: {
		message_template: string;
		content: string;
		status_code: number;
	};
	metadata: {
		request_id: string;
	};
};
export type NLCreateAgentStreamMessage =
	| NLCreateAgentStreamDraftAgent
	| NLCreateAgentStreamAnswerPart
	| NLCreateAgentStreamFinalResponse
	| NLCreateAgentStreamError;

// ************************************************************************************************
// Other types
// ************************************************************************************************

// All responses should be converted into StreamResponse
// - AssistantMessage -> StreamResponse
// - AgentResponse -> StreamResponse
export type RawChatMessage = HumanMessage | AssistantMessage;
export type RawChat = RawChatMessage[];

export type ChatMessage = HumanMessage | StreamResponse;
export type Chat = ChatMessage[];

export type AssistanceServiceProduct =
	| 'confluence'
	| 'jira-software'
	| 'jira-core'
	| 'jsm'
	| 'jpd'
	| 'atlas'
	| 'jira'
	| 'unknown'
	| 'rovo'
	| 'bitbucket'
	| 'trello'
	| 'elevate'
	| 'recruit'
	| 'mercury'
	| 'admin'
	| ''
	| string;

export type AssistanceServiceConfig = {
	baseUrl?: string | undefined;
	headers?: Record<string, string>;
	/**
	 * Optional value for the `X-Channel-Id` header on chat-related requests.
	 * Defaults to the service-specific channel ID when omitted.
	 */
	channelId?: string;
	product: AssistanceServiceProduct;
	credentials?: RequestInit['credentials'];
	experienceId: string;
	minionAlias?: string;
	labelStack?: LabelStack;
	/**
	 * Optional override for the path used by `validateFiles`. Defaults to
	 * `/rovo/v1/chat/message/file/validate` when not provided.
	 */
	fileValidatePath?: string;
	/**
	 * Optional override for the path used by `uploadFiles`. Defaults to
	 * `/rovo/v1/chat/message/file` when not provided.
	 */
	fileUploadPath?: string;
};

export type ExternalFile = {
	media_file_id: string;
	file_name: string;
	mime_type: string;
};

export type PostAgentPayload = Pick<
	Agent,
	| 'name'
	| 'description'
	| 'named_id'
	| 'system_prompt_template'
	| 'available_plugins'
	| 'visibility'
	| 'external_files'
	| 'user_defined_conversation_starters'
	| 'knowledge_sources'
	| 'action_config'
	| 'creator'
>;

export type ConversationChannelStatus = 'OPEN' | 'ENDED' | 'EXPIRED' | 'ESCALATED';

export type MessageType = 'DEEP_RESEARCH' | 'WEB_SEARCH';

export type ConversationChannelMessageStatus =
	| 'IN_PROGRESS'
	| 'SUCCESSFUL'
	| 'FAILED'
	| 'CANCELLED';

export type LastAgentMessageStatus = 'IN_PROGRESS' | 'SUCCESSFUL' | 'FAILED' | 'CANCELLED';

export type ConversationChannelScope = {
	type: 'image' | 'file' | 'jira' | 'confluence' | 'link';
	name: string;
	link: string;
	icon_url?: string;
	sub_type?: 'space' | 'page' | 'whiteboard';
};

export type ConversationChannelTask = {
	task_id: string;
	conversation_id: string;
	message_id: string | null;
	completion_message_id: string | null;
	current_message_id: string | null;
	agent_id: string | null;
	status: ApiTaskStatus;
	trigger_type: 'EXPLICIT' | 'IMPLICIT';
	title: string;
	plan: { version: number; text: string } | null;
	result_summary: string | null;
	error_message: string | null;
	created_at: string;
	started_at: string | null;
	updated_at: string;
	completed_at: string | null;
	execution_segments: Record<string, string> | null;
	todo_list: Record<string, string> | null;
};

export type ConversationChannel = {
	readStatus?: boolean | null;
	id: string;
	experience_id: string;
	owner: string;
	time_created: string;
	status: ConversationChannelStatus;
	name?: string;
	messages?: Message[];
	last_message?: string;
	last_message_type?: MessageType[];
	last_message_timestamp?: string;
	last_agent_message_status?: ConversationChannelMessageStatus;
	assistant_message_id?: string;
	agent?: AgentMetadata;
	scopes?: ConversationChannelScope[];
	tags?: string[];
	tasks?: ConversationChannelTask[];
};

export type ConversationChannelsRequest = {
	limit?: number;
	cursor?: string;
};

export type ConversationChannelsResponse = {
	items: ConversationChannel[];
	hasNext: boolean;
	cursor: string | null;
};

export type AgentMetadata = {
	agent_id: string;
	agent_named_id: string;
	agent_name: string;
	identity_account_id?: string;
	creator_type: string;
	forge_url?: string;
};

export interface MessagePayloadAction {
	key: string;
	invocation_id: string;
	planned_data: Record<string, unknown>;
	status: 'PLANNED' | 'STARTED' | 'FINISHED' | 'CANCELLED' | 'ERRORED';
	action_id?: string;
	metadata?: Record<string, unknown>;
	tool_source?: string;
	confirmation_message?: string;
	human_message_id?: string;
	display_name: string;
}

export interface Dialogues {
	human_message: {
		content: string;
	};
	agent_message: {
		content: string;
		/** MIME type of the agent message content.
		 * Use `'text/adf'` when content is ADF JSON.
		 * Sent to the backend API so it stores the content type correctly. */
		mimeType?: 'text/markdown' | 'text/adf';
		/** Actions to be included in the agent message.
		 * Allows creating conversations with pre-populated action data (e.g., template flow). */
		actions?: MessagePayloadAction[];
	};
}

export type SendMessageDashboardContext =
	| {
			content: string;
	  }
	| undefined;

export type SendMessageDashboardEditorContext =
	| {
			content: string;
			dashboardAri?: string;
			isEditing?: boolean;
	  }
	| undefined;

export type SendMessageEditorContext =
	| {
			// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
			document: {
				type: 'text/markdown' | 'text/adf';
				content: string;
			};
			selection: {
				type: 'text/markdown' | 'text/plain';
				content: string;
			};
	  }
	| undefined;

export type SendMessageWhiteboardContext =
	| {
			type: 'image/svg+xml' | 'text/plain';
			content: string;
			contentId?: string;
	  }
	| undefined;

export type SendMessageSlidesContext =
	| {
			xml: string;
			contentId: string;
			title: string;
			url: string;
			selectedSlideIndex: number;
			selectedElementIds: string[];
			isViewMode?: boolean;
	  }
	| undefined;

export type SendMessageDatabaseContext =
	| {
			contentId: string;
			csv: string;
			title: string;
			url: string;
			selectedElementIds?: string[];
			isViewMode?: boolean;
	  }
	| undefined;

export type SendMessageChatContext =
	| {
			[contextKey: string]: unknown;
	  }
	| undefined;

export type SendMessageBrowserContext = {
	context:
		| {
				browserUrl: string;
				htmlBody?: string;
				canvasText?: string;
		  }
		| undefined;
};

export type AlphaExtendedThinkingMode = 'MARATHON' | 'LONG_HORIZON';

export type SendMessageSearchArtifact = {
	/** The Atlassian Resource Identifier (ARI) of the object. */
	ari?: string;
	/** The URL of the object. */
	url?: string;
};

export type SendMessageWorkflowContext = {
	currentWorkflowDocument?: Record<string, unknown>;
};

export type PermissionId =
	| 'AGENT_CREATE'
	| 'AGENT_READ'
	| 'AGENT_UPDATE'
	| 'AGENT_DEACTIVATE'
	| 'AGENT_DELETE';

export type GetAgentPermissionsPayload = {
	permission_ids: PermissionId[];
};

export type AgentPermissionResponse = {
	permissions: Partial<
		Record<
			PermissionId,
			{
				permitted: boolean;
			}
		>
	>;
};

export type FileUploadResponseItem = {
	id: string;
	conversation_channel_id?: string;
	blob_id?: string;
	name: string;
	size?: number;
	type?: string;
	created_at?: string;
	owner?: string;
};

export type FileMetadataResponseItem = {
	id: string;
	conversation_channel_id: string;
	blob_id: string;
	name?: string;
	size: number;
	type?: string;
	created_at: string;
	owner: string;
};

export type FileUploadResponse = {
	data: FileUploadResponseItem[];
};

export type FileValidationMetadata = {
	originalFilename: string;
	contentType: string;
	size: number;
};

export type FileValidationPayload = {
	fileMetadata: FileValidationMetadata[];
};

export type FileValidationResponse = {
	validationResults: {
		fileMetadata: FileValidationMetadata;
		isSupported: boolean;
		errorCode?:
			| 'INVALID_CONTENT_TYPE'
			| 'FILE_TOO_LARGE'
			| 'FILE_NAME_MISSING'
			| 'UNKNOWN_ERROR'
			| string;
	}[];
};

export type RovoLabsTool = {
	name: string;
	displayName: string;
	description: string;
	/**
	 * This is the JSON schema for the tool's configuration.
	 */
	configurationInJsonSchema?: unknown;
	icon?: string;
	provider: RovoLabsToolProvider;
	integrationKey?: string;
};

export type RovoLabsToolProvider = {
	id: string;
	displayName: string;
};

export type RovoLabsToolPayload = {
	name: string;
	configuration: unknown;
	provider: RovoLabsToolProvider;
};

export type RovoLabsChatStreamPayload = {
	latest_user_message: string | DocNode;
	conversation_id: string;
	workflow_configuration: {
		routerConfig: {
			maxLoop: number;
			selectedTools: RovoLabsToolPayload[];
		};
		toolInvocationConfig: {
			toolInvocationTimeoutMs: number;
		};
		selectedLanguageModelId?: string;
		promptConfig?: {
			systemPromptTemplate?: string;
			includeCurrentUserContext: boolean;
		};
	};
	additional_context?: {
		file_attachment_ids: string[];
	};
	confirmed_tools?: RovoLabsConfirmedTools;
};

export type RovoLabsLanguageModel = {
	modelId: string;
	modelProvider: string;
};

export type RovoLabsDefaultPrompt = {
	defaultSystemPromptTemplate: string;
};

export type RovoLabsConfirmedTool = {
	key: string;
	tool_id?: string;
	tool_source?: string;
	invocation_id: string;
	data: {
		human_message_id: string;
		arguments: unknown;
	};
	resolution?: {
		type?: 'confirmed' | 'cancelled';
	};
};

export type RovoLabsConfirmedTools = {
	message_id: string;
	tools: RovoLabsConfirmedTool[];
};

export type StreamToolSelection = {
	type: 'TOOL_SELECTION';
	message: {
		invocationId: string;
		displayName: string;
		toolInput: unknown;
	};
	millisOffset?: number;
	metadata?: {
		request_id?: string;
	};
};

export type StreamToolInvocation = {
	type: 'TOOL_INVOCATION';
	message: {
		invocationId: string;
		displayName: string;
		toolOutput: unknown;
	};
	millisOffset?: number;
};

/**
 * Interface for the Assistance Service.
 */
/**
 * Version info sent to the BE when sending messages.
 * Matches the BE's AgentVersionInput shape.
 */
export type AgentVersionInput = {
	versionId?: string;
	versionType?: string;
};

/**
 * Request-context fields shared by `sendMessageStream` and `sendMessageConfirmedTools`.
 * Both methods POST to the same message-stream endpoint and serialize an identical
 * `body.context`, so the context-shaped payload fields are defined once here to keep the
 * two signatures in sync. Method-specific fields (e.g. `fileIds`, `appFilters` on
 * `sendMessageStream`) stay inline on their respective payloads.
 */
export interface SendMessageContextParams {
	jiraWorkflowContext?: SendMessageWorkflowContext;
	editorContext?: SendMessageEditorContext;
	whiteboardContext?: SendMessageWhiteboardContext;
	slidesContext?: SendMessageSlidesContext;
	databaseContext?: SendMessageDatabaseContext;
	browserContext?: SendMessageBrowserContext;
	additionalContext?: Record<string, unknown>;
	aiFeatureContext?: SendMessageChatContext;
	dashboardContext?: SendMessageDashboardContext;
	dashboardEditorContext?: SendMessageDashboardEditorContext;
	/**
	 * This when enabled will ensure that the browser_url is always used by the Rovo as context
	 */
	useCurrentBrowsingContext?: boolean;
	/**
	 * Sanitizer for browser context URLs.
	 * Used to check if a URL should be blocked from being used as browser context.
	 * If a URL is blocklisted, an empty URL and empty context will be sent instead.
	 */
	browserContextSanitizer?: {
		isBlocklisted: (url: string) => boolean;
	};
	/**
	 * Optional artifact representing the object the user is currently interacting with or
	 * acting on (e.g. 3P forge artifacts that are not resolvable without an ARI).
	 * Sent as `search_artifact` in `body.context`.
	 */
	searchArtifact?: SendMessageSearchArtifact;
}

/**
 * Visibility mode for a shared conversation snapshot.
 *
 * - `ORG`: anyone in the user's organization with the share link can view.
 * - `SPECIFIC_USERS`: only the listed user ARIs can view.
 *
 */
export type ConversationShareType = 'ORG' | 'SPECIFIC_USERS';

/**
 * Request body for `PATCH /api/v2/share`.
 *
 * Backend treats `(conversationId, messageId)` as an idempotency key: calling
 * with the same pair returns the previously-issued `shareToken` instead of
 * minting a new one. To produce a new snapshot, advance the conversation and
 * call again with the new latest assistant `messageId`.
 *
 * Validation rules enforced by the backend:
 * - `shareType === 'SPECIFIC_USERS'` requires a non-empty `addUserAris`.
 * - `shareType === 'ORG'` must omit `addUserAris` entirely.
 */
export type UpsertConversationShareRequest = {
	/** Conversation being shared. */
	conversationId: string;
	/**
	 * Latest assistant message id to include in the snapshot. The snapshot
	 * contains every message up to and including this one; later messages
	 * are excluded.
	 */
	messageId: string;
	shareType: ConversationShareType;
	/**
	 * ARIs of the users granted access. Required when
	 * `shareType === 'SPECIFIC_USERS'`; must be omitted otherwise.
	 */
	addUserAris?: string[];
};

/**
 * Response body returned by `PATCH /api/v2/share`. The backend returns 201
 * when a new share is created and 200 when the existing share for
 * `(conversationId, messageId)` is returned or updated.
 */
export type ConversationShareResponse = {
	/** Opaque token used to construct the shareable URL. */
	shareToken: string;
	shareType: ConversationShareType;
	/** ISO-8601 timestamp of when the share was first created. */
	createdAt: string;
	/**
	 * ARIs allowed to view the share. `null` when `shareType === 'ORG'`
	 * (anyone in the org can view).
	 */
	allowedUserAris: string[] | null;
};

export interface AssistanceService {
	sendMessageStream: (
		payload: {
			conversationId: string;
			message: string | DocNode;
			mimeType?: string;
			/**
			 * This can be agentId or agentNamedId, and BE will resolve it correctly
			 * https://atlassian.slack.com/archives/C0677M19GU9/p1725952045906819?thread_ts=1725929736.588339&cid=C0677M19GU9
			 *
			 * As of RAGE-60 the default agent is no longer required to be sent to the backend. So agentNamedId can be undefined.
			 */
			agentNamedId?: string;
			agentId?: string;
			controller?: AbortController;
			storeMessage?: boolean;
			/** Whether to keep the source indexes in the message e.g [^1^], [^2^] */
			citationsEnabled?: boolean;
			minionAliasConfig?: Record<string, unknown>;
			fileIds?: string[];
			deepResearchEnabled?: boolean;
			thinkDeeperEnabled?: boolean;
			alphaExtendedThinkingMode?: AlphaExtendedThinkingMode;
			fastModeEnabled?: boolean;
			planModeEnabled?: boolean;
			webSearchEnabled?: boolean;
			/**
			 * appFilters being undefined means all org knowledge is applied.
			 * appFilters being empty means no org knowledge is applied.
			 * appFilters being non-empty means some org knowledge is applied.
			 */
			appFilters?: string[];
			optInToDeepResearchV2?: boolean;
			optInThinkDeeperMessages?: boolean;
			optInAnsNow?: boolean;
			optInNewToolTraces?: boolean;
			optInNewTeamworkTraceIcons?: boolean;
			optInSSEStream?: boolean;
			agentVersion?: AgentVersionInput;
			spaceId?: string;
			/**
			 * A list of capability strings the client is opting in to.
			 * Sent as the `X-Rovo-Chat-Capabilities` header (JSON array).
			 * When undefined or empty the header is omitted for backward compatibility.
			 */
			capabilities?: string[];
			/**
			 * When provided, controls whether BE should extract conversation scopes from current message context.
			 */
			shouldExtractConversationScopes?: boolean;
			parentMessageId?: string;
		} & SendMessageContextParams,
	) => Promise<FetchStreamReturn<StreamMessage, boolean>>;
	reconnectToMessageStream: (payload: {
		conversationId: string;
		messageId: string;
		lastEventId?: string;
		controller?: AbortController;
	}) => Promise<FetchStreamReturn<StreamMessage, true>>;
	/**
	 * Cancel an async streaming task for a message.
	 * Calls the backend API to stop processing the request.
	 */
	cancelMessageStream: (payload: { conversationId: string; messageId: string }) => Promise<void>;
	sendMessageConfirmedTools: (
		payload: {
			conversationId: string;
			agentId?: string;
			controller?: AbortController;
			/**
			 * This is used as an alternative way to execute action/tools
			 * replacing the need to go through `/action` endpoint
			 * https://hello.atlassian.net/wiki/spaces/agents/pages/5974706697/Short-term+Enable+resumption+for+external+MCP
			 */
			confirmed_tools: RovoLabsConfirmedTools;
			optInSSEStream?: boolean;
			storeMessage?: boolean;
			webSearchEnabled?: boolean;
			deepResearchEnabled?: boolean;
			thinkDeeperEnabled?: boolean;
			alphaExtendedThinkingMode?: AlphaExtendedThinkingMode;
			fastModeEnabled?: boolean;
			planModeEnabled?: boolean;
			agentVersion?: AgentVersionInput;
			optInToDeepResearchV2?: boolean;
			optInThinkDeeperMessages?: boolean;
			optInAnsNow?: boolean;
			optInNewToolTraces?: boolean;
			optInNewTeamworkTraceIcons?: boolean;
			/**
			 * A list of capability strings the client is opting in to.
			 * Sent as the `X-Rovo-Chat-Capabilities` header (JSON array).
			 * When undefined or empty the header is omitted for backward compatibility.
			 */
			capabilities?: string[];
		} & SendMessageContextParams,
	) => Promise<FetchStreamReturn<StreamMessage, boolean>>;
	nlCreateAgentStream: (
		payload: {
			controller?: AbortController;
		} & NLCreateAgentPayload,
	) => Promise<FetchStreamReturn<NLCreateAgentStreamMessage>>;
	getConfig: () => AssistanceServiceConfig;
	getChat: (conversationId: string) => Promise<Chat>;
	deleteChat: (conversationId: string) => Promise<void>;
	getAgents: () => Promise<Agent[]>;
	getAgentDetails: (id: string) => Promise<AgentDetails>;
	getAgentDetailsByIdentityAccountId: (identityAccountId: string) => Promise<AgentDetails>;
	createAgent: (payload: PostAgentPayload) => Promise<Agent>;
	updateAgent: (id: string, payload: PostAgentPayload) => Promise<Agent>;
	deleteAgent: (id: string) => Promise<void>;
	favouriteAgent: (id: string) => Promise<void>;
	unfavouriteAgent: (id: string) => Promise<void>;
	getConversationChannels: (
		options?: ConversationChannelsRequest,
	) => Promise<ConversationChannelsResponse>;
	createConversationChannel: (options?: {
		name?: string;
		dialogues?: Dialogues[];
		tags?: string[];
	}) => Promise<ConversationChannel & Required<Pick<ConversationChannel, 'name'>>>;
	updateConversationChannel: (
		id: string,
		payload: Partial<ConversationChannel>,
	) => Promise<ConversationChannel>;
	resolveConversationAction: (
		conversationId: string,
		payload: MessageActionPayload,
	) => Promise<MessageActionResponse>;

	bulkResolveConversationUIActions: (
		conversationId: string,
		messageId: string,
		payload: BulkMessageActionPayload,
	) => Promise<BulkMessageActionResponse>;

	// REST conversation starters, newer API
	generateAgentStarters: (payload: {
		agent_name?: string;
		description?: string;
		system_prompt?: string;
	}) => Promise<ConversationStarterResponse>;

	// REST conversation starters, newer API
	generateContextualStarters: (payload: {
		browser_url: string;
	}) => Promise<ConversationStarterResponse>;

	// REST dynamic conversation starters (new endpoint. /convo-starters)
	getDynamicConvoStartersNew: (
		payload: DynamicConversationStarterRequestNew,
	) => Promise<DynamicConversationStarterResponseNew>;

	getAgentActions: () => Promise<AgentAction[]>;
	getAgentKnowledgeConfiguration: (
		agentCreatorType?: AgentCreatorType,
	) => Promise<AgentKnowledgeConfigurationResponse>;
	getAgentPermissions: (
		agentId: string,
		payload: GetAgentPermissionsPayload,
	) => Promise<AgentPermissionResponse>;
	getRecommendedAgents: (payload: { limit: number; pageUrl?: string }) => Promise<{
		agents: Agent[];
		metadata: { recommendationKey: string };
	}>;
	getConversationDebugLogs: (conversationId: string) => Promise<Chat>;
	uploadFiles: (payload: { files: File[] }) => Promise<FileUploadResponse>;
	validateFiles: (payload: FileValidationPayload) => Promise<FileValidationResponse>;
	rovoLabsSendMessage: (
		payload: RovoLabsChatStreamPayload,
	) => Promise<FetchStreamReturn<StreamMessage | StreamToolSelection | StreamToolInvocation>>;
	rovoLabsGetConversations: () => Promise<{ items: ConversationChannel[] }>;
	rovoLabsGetTools: () => Promise<{ tools: RovoLabsTool[] }>;
	rovoLabsGetLanguageModels: () => Promise<{ languageModels: RovoLabsLanguageModel[] }>;
	rovoLabsGetPrompts: () => Promise<RovoLabsDefaultPrompt>;
	getWebSearchConfiguration: () => Promise<WebSearchConfigurationResponse>;
	getPrompts: () => Promise<GetAllPromptsResponse>;
	getPrompt: (promptId: string) => Promise<GetPromptResponse>;
	bulkGetPrompts: (payload: BulkGetPromptsRequest) => Promise<BulkGetPromptsResponse>;
	createPrompt: (payload: CreatePromptRequest) => Promise<GetPromptResponse>;
	updatePrompt: (promptId: string, payload: UpdatePromptRequest) => Promise<GetPromptResponse>;
	deletePrompt: (shareId: string) => Promise<void>;
	addPromptsToLibrary: (payload: AddPromptsToLibraryRequest) => Promise<void>;
	favoritePrompts: (payload: FavoritePromptsRequest) => Promise<void>;
	bulkUpdatePrivacy: (payload: BulkUpdatePrivacyRequest) => Promise<BulkUpdatePrivacyResponse>;
	upsertConversationShare: (
		payload: UpsertConversationShareRequest,
	) => Promise<ConversationShareResponse>;
	getGeneratedContent: (payload?: {
		actions?: string[];
		statuses?: string[];
		limit?: number;
	}) => Promise<ConversationContent[]>;
	getRovoUserSettings: () => Promise<RovoUserSettings>;
	updateRovoUserSettings: (settings: Partial<RovoUserSettings>) => Promise<RovoUserSettings>;
	getRovoMemories: () => Promise<RovoMemoriesResponse>;
	getRovoMemoryBlurb: () => Promise<RovoMemoryBlurbResponse>;
	getRovoMemoryEdits: () => Promise<RovoMemoryEditsResponse>;
	getRovoMemoryEditsCount: () => Promise<RovoMemoryEditsCountResponse>;
	deleteRovoMemoryEdit: (editId: string) => Promise<void>;
	answerNow: (conversationId: string, messageId: string) => Promise<void>;
	deleteRovoMemory: (memoryId: string) => Promise<void>;
	createMemoryEdit: (payload: MemoryEditRequest) => Promise<MemoryEditPromptResponse>;
	createMemory: (payload: CreateMemoryRequest) => Promise<CreateMemoryResponse>;
	fetchConversationMetadata: (conversationId: string) => Promise<ConversationChannel>;
	fetchPersonalizedPromptSuggestions: (config: {
		offset?: number;
		limit?: number;
	}) => Promise<SuggestedPromptTile[]>;
	fetchPersonalizedPromptSuggestionsStream: (config: {
		offset?: number;
		limit?: number;
	}) => Promise<FetchStreamReturn<SuggestedPromptTileStreamMessage>>;
	getTasks: (params?: { limit?: number; cursor?: string }) => Promise<ApiTaskListResponse>;
	getTasksStream: (
		controller?: AbortController,
	) => Promise<FetchStreamReturn<ApiTaskSSEUpdate, true>>;
	cancelTask: (taskId: string) => Promise<void>;
	deleteTask: (taskId: string) => Promise<void>;
	renameTask: (taskId: string, title: string) => Promise<ApiTask>;
}

export type ApiTaskStatus =
	| 'DRAFT'
	| 'QUEUED'
	| 'RUNNING'
	| 'AWAITING_DISAMBIGUATION'
	| 'AWAITING_PLAN_CONFIRMATION'
	| 'AWAITING_ACTION_CONFIRMATION'
	| 'COMPLETED'
	| 'FAILED'
	| 'CANCELLED';

export type ApiTask = {
	task_id: string;
	conversation_id: string;
	message_id: string | null;
	completion_message_id?: string | null;
	current_message_id?: string | null;
	agent_id: string | null;
	status: ApiTaskStatus;
	trigger_type: 'EXPLICIT' | 'IMPLICIT';
	title: string;
	plan: {
		version: number;
		text: string;
	} | null;
	result_summary: string | null;
	error_message: string | null;
	created_at: string;
	started_at: string | null;
	updated_at: string;
	completed_at: string | null;
	execution_segments?: Record<string, string> | null;
};

export type ApiTaskListResponse = {
	tasks: ApiTask[];
	total_count: number | null;
	cursor: string | null;
};

// ************************************************************************************************
// Tasks SSE Types
// ************************************************************************************************

export type ApiTaskSSEEventType =
	| 'connected'
	| 'task_status'
	| 'task_snapshot'
	| 'task_todo_update';

export type ApiTaskCurrentStepStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';

export type ApiTaskCurrentStep = {
	id: string;
	content: string;
	status: ApiTaskCurrentStepStatus;
	priority?: 'LOW' | 'MEDIUM' | 'HIGH';
};

/**
 * Represents the `task_snapshot` event — emitted on connect with the current state
 * of all active tasks for the user.
 *
 * Example payload:
 * {
 *   "tasks": [
 *     { "task_id": "ee803e7a-...", "title": "...", "status": "DRAFT", ... }
 *   ]
 * }
 */
export type ApiTaskSnapshotSSEUpdate = {
	tasks: ApiTaskSSEUpdate[];
};

/**
 * Represents a single Task Status event emitted by the Tasks SSE stream
 * (GET /rovo/v1/tasks/stream).
 *
 * The `event_type` field is carried as a separate SSE envelope field and is
 * not part of the event data payload itself.
 *
 * Example payload:
 * {
 *   "task_id": "ari:cloud:convo-ai::task/abc-123",
 *   "title": "Create QBR narrative from Q1 data",
 *   "status": "RUNNING",
 *   "current_step": { "id": "step-2", "content": "Analyze trends", "status": "IN_PROGRESS", "priority": "HIGH" },
 *   "result_summary": null,
 *   "error_message": null,
 *   "message_id": "msg-789",
 *   "completion_message_id": null,
 *   "created_at": "2026-04-02T10:30:00Z",
 *   "started_at": "2026-04-02T10:30:05Z",
 *   "updated_at": "2026-04-02T10:35:12Z"
 * }
 */
export type ApiTaskSSEUpdate = {
	task_id: string;
	title: string;
	status: ApiTaskStatus;
	current_step: ApiTaskCurrentStep | null;
	result_summary: string | null;
	error_message: string | null;
	conversation_id?: string | null;
	message_id?: string | null;
	completion_message_id?: string | null;
	current_message_id?: string | null;
	created_at?: string | null;
	started_at?: string | null;
	updated_at: string;
	execution_segments?: Record<string, string> | null;
	todo_list?: Record<string, string> | null;
};

// ************************************************************************************************
// Type Guards
// ************************************************************************************************

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isChatMessage = (message: any): message is ChatMessage => {
	return isHumanMessage(message) || isAssistantMessage(message) || isStreamResponse(message);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isHumanMessage = (message: any): message is HumanMessage =>
	message instanceof Object && message.role === 'HUMAN';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isAssistantMessage = (message: any): message is AssistantMessage =>
	message instanceof Object && message.role === 'ASSISTANT';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isAgentResponse = (message: any): message is AgentResponse =>
	message instanceof Object && isAssistantMessage(message.message);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isInProgressStreamResponse = (message: any): message is StreamResponse =>
	isStreamResponse(message) && message.message?.message?.async_status === 'IN_PROGRESS';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamResponse = (message: any): message is StreamResponse =>
	message instanceof Object && message.type === 'FINAL_RESPONSE';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamError = (message: any): message is StreamError =>
	message instanceof Object && message.type === 'ERROR';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamAUPError = (message: any): message is StreamError =>
	isStreamError(message) && message.message?.message_template === ACCEPTABLE_USE_VIOLATIONS_ERROR;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamAnswerPart = (message: any): message is StreamAnswerPart =>
	message instanceof Object && message.type === 'ANSWER_PART';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamReasoningPart = (message: any): message is StreamReasoningPart =>
	message instanceof Object && message.type === 'REASONING_PART';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamActionPart = (message: any): message is StreamActionPart =>
	message instanceof Object && message.type === 'ACTION_PART';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamActionClassification = (message: any): message is StreamActionClassification =>
	message instanceof Object && message.type === 'ACTION_CLASSIFICATION';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamActionPrepare = (message: any): message is StreamActionPrepare =>
	message instanceof Object && message.type === 'ACTION_PREPARE';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamActionComplete = (message: any): message is StreamActionComplete =>
	message instanceof Object && message.type === 'ACTION_COMPLETE';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamResearchStart = (message: any): message is StreamResearchStart =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'RESEARCH_START';

export const isStreamResearchBeginningTrace = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	message: any,
): message is StreamResearchStepContent =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'RESEARCH';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamResearchStepContent = (message: any): message is StreamResearchStepContent =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'RESEARCH_STEP';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamResearchStepTitle = (message: any): message is StreamResearchStepTitle =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'RESEARCH_STEP_TITLE';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamResearchPlan = (message: any): message is StreamResearchPlan =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'RESEARCH_PLAN';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamTodoSnapshot = (message: any): message is StreamTodoSnapshot =>
	message instanceof Object && message.type === 'TODO_SNAPSHOT';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamTaskStatus = (message: any): message is StreamTaskStatus =>
	message instanceof Object && message.type === 'TASK_STATUS';

export const isStreamResearchStepTitleShort = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	message: any,
): message is StreamResearchStepTitleShort =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'RESEARCH_STEP_TITLE_SHORT';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamSkillStart = (message: any): message is StreamSkillStart =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'SKILL_START';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamSkillEnd = (message: any): message is StreamSkillEnd =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'SKILL_END';

export const isStreamFastModeResponseAvailable = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	message: any,
): message is StreamFastModeResponseAvailable =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'FAST_MODE_RESPONSE_AVAILABLE';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamFastModeEnabled = (message: any): message is StreamFastModeEnabled =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'FAST_MODE_ENABLED';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamRemoveAnswerNow = (message: any): message is StreamRemoveAnswerNow =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'REMOVE_ANSWER_NOW';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamResearchStepSource = (message: any): message is StreamResearchStepSource =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'RESEARCH_STEP_SOURCE';

export const isStreamResearchStepCompleted = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	message: any,
): message is StreamResearchStepCompleted =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'RESEARCH_STEP_COMPLETE';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamResearchStepIcons = (message: any): message is StreamResearchStepIcons =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'RESEARCH_STEP_ICONS';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamResearchStepDocument = (message: any): message is StreamResearchStepDocument =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'RESEARCH_STEP_DOCUMENT';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamReconnectSupportPart = (message: any): message is StreamReconnectSupportPart =>
	message instanceof Object && message.type === 'RECONNECT_SUPPORTED';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamAnswerGenerationType = (message: any): message is SteamAnsGenerationType =>
	message instanceof Object && message.type === 'ANSWER_GENERATION_TYPE';

export const isStreamDynamicLayoutSubAgent = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	message: any,
): message is StreamDynamicLayoutSubAgent =>
	message instanceof Object && message.type === 'DL_SUB_AGENT';

export const isStreamResearchPayload = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	message: any,
): message is
	| StreamResearchTrace
	| StreamResearchStepContent
	| StreamResearchStepTitle
	| StreamResearchStepTitleShort
	| StreamResearchStepSource
	| StreamResearchStepCompleted
	| StreamResearchStepIcons
	| StreamResearchStepDocument =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	(message.message.message_template === 'RESEARCH' ||
		message.message.message_template === 'RESEARCH_STEP' ||
		message.message.message_template === 'RESEARCH_STEP_TITLE' ||
		message.message.message_template === 'RESEARCH_STEP_TITLE_SHORT' ||
		message.message.message_template === 'RESEARCH_STEP_SOURCE' ||
		message.message.message_template === 'RESEARCH_STEP_COMPLETE' ||
		message.message.message_template === 'RESEARCH_STEP_ICONS' ||
		message.message.message_template === 'RESEARCH_STEP_DOCUMENT');

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamSkill = (message: any): boolean =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	(isStreamSkillStart(message) || isStreamSkillEnd(message));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamMemoryUpdate = (message: any): message is StreamMemoryUpdateTrace =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message?.message_template === 'MEMORY_UPDATE';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamTrace = (message: any): message is StreamTrace => {
	// TRACE and research steps are sharing the same type, but we want to separate them
	if (isStreamResearchPayload(message)) {
		return false;
	}

	if (isStreamResearchPlan(message)) {
		return false;
	}

	if (isStreamSkill(message)) {
		return false;
	}

	if (isStreamFastModeResponseAvailable(message)) {
		return false;
	}

	if (isStreamThinkDeeperTriggered(message)) {
		return false;
	}

	return message instanceof Object && message.type === 'TRACE';
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamThinkDeeperTriggered = (message: any): boolean => {
	return (
		message instanceof Object &&
		message.type === 'TRACE' &&
		message.message?.message_template === 'THINK_DEEPER_TRIGGERED'
	);
};

export const isFollowUpMessage = (message: unknown): message is StreamFollowUp =>
	message !== undefined &&
	message !== null &&
	typeof message === 'object' &&
	'type' in message &&
	message.type === 'FOLLOW_UP_QUERIES';

export const isStreamAgenticUi = (
	message: unknown,
): message is ParsedSSEMessage<StreamAgenticUi> => {
	if (!isSSEMessage<unknown>(message) || message.eventType !== 'AGENTIC_UI') {
		return false;
	}

	if (message.data === null || typeof message.data !== 'object') {
		return false;
	}

	const data = message.data as { message?: { content?: unknown } };
	return Array.isArray(data.message?.content);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamHeartbeat = (message: any): message is StreamHeartbeat =>
	message instanceof Object && message.type === 'HEART_BEAT';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isActionExecutedSuccessfully = (message: any): boolean =>
	isAssistantMessage(message) &&
	(message?.message_metadata?.is_action_execution ?? false) &&
	message?.message_metadata?.action_execution_result === 'SUCCESS';

export const isActionResolutionCancelled = (
	payloadContext: MessageActionPayloadContext,
): boolean => {
	return payloadContext?.resolution?.type === 'cancel';
};

export const isChannelNotFoundError = (error: unknown): boolean =>
	isApiError(error) && error.errorAttributes?.errorCode === CONVERSATION_CHANNEL_NOT_FOUND_ERROR;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamActionError = (msg: any): msg is StreamActionError =>
	msg && msg.type === 'ACTION_ERROR' && msg.message && msg.message.status === 'ERRORED';

export const isRateLimitedError = (error: unknown): boolean =>
	isApiError(error) && error.errorAttributes.statusCode === 429;

export const isHipaaError = (error: unknown): boolean =>
	isApiError(error) &&
	!!error.errorAttributes.errorCode &&
	error.errorAttributes.errorCode === ASSISTANCE_SERVICE_ERROR_CODES.HAS_HIPAA_ENABLED;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isAUPViolationError = (error: any): boolean =>
	error instanceof Object &&
	error?.detail?.type === 'ERROR' &&
	!!error.errorAttributes.errorCode &&
	error.errorAttributes.errorCode === ACCEPTABLE_USE_VIOLATIONS_ERROR;

export const isConversationChannelDataMessage = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	message: any,
): message is ConversationChannelDataMessage =>
	message instanceof Object && message.type === 'CONVERSATION_CHANNEL_DATA';

export const isScopeDeterminedMessage = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	message: any,
): message is ScopeDeterminedMessage =>
	message instanceof Object && message.type === 'SCOPE_DETERMINED';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamPromptRedaction = (message: any): message is StreamPromptRedaction =>
	message instanceof Object &&
	(message.type === 'PROMPT_REDACTION' || message.type === 'PROMPT_REDACTED');

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamPromptBlock = (message: any): message is StreamPromptBlock =>
	message instanceof Object &&
	(message.type === 'PROMPT_BLOCKED' || message.type === 'PROMPT_BLOCK');

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamResponseRedaction = (message: any): message is StreamResponseRedaction =>
	message instanceof Object && message.type === 'RESPONSE_REDACTION';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamResponseBlock = (message: any): message is StreamResponseBlock =>
	message instanceof Object && message.type === 'RESPONSE_BLOCKED';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamAttachmentBlock = (message: any): message is StreamAttachmentBlock =>
	message instanceof Object && message.type === 'ATTACHMENT_BLOCKED';

export function isConfirmAction(payloadContext: MessageActionPayloadContext): boolean {
	return payloadContext?.resolution?.type === 'confirm';
}

export type Source = {
	ari: string;
	title: string;
	type: string;
	url: string;
	lastModified: string;
	/** @private @deprecated this is no longer being returned by BE, keeping it because some legacy unified-help code is still using it */
	id?: number;
	product?: string;
};

export type Sources = Array<Source>;

/**
 * BE-provided classification for why no content was created in the AIFC staging area.
 * The FE uses these to route the experience between `fail` and `abort` and to choose
 * the variant of the no-content-created screen. Missing or unrecognised values are
 * treated as a fail (error screen).
 */
export type NoCreateClassification =
	| 'BAD_ROVO_REPLY'
	| 'LIED_ABOUT_CREATION'
	| 'BAD_USER_PROMPT'
	| 'GOOD_REPLY_NO_CREATE'
	| 'EXTERNAL_ERROR';

export type Metadata = {
	deep_research?: boolean;
	interaction_mode_requested?: string;
	interaction_mode_executed?: string;
	// Unified Help Only
	bm25Variant?: string;
	confidenceScore?: number;
	isFallbackMessage?: boolean;
	originalQuery?: string;
	semanticSearchLocationVariant?: string;
	semanticSearchVariant?: string;
	action_execution_result?: string;
	is_action_execution?: boolean;
	invocation_id?: string;
	remote_entity_id?: string;
	requestId?: string;
	actionOutput?: {
		issueId?: string;
		url?: string;
		conversationId?: string;
		_originalResponse?: {
			requestUrl?: string;
			conversationAri?: string;
			channelAri?: string;
			availability?: {
				outcome?: string;
				reason?: string;
				criteria?: string;
			};
			issueKey?: string;
		};
	};
	links?: {
		base?: string;
		webui?: string;
	};
	orchestrator_depth_limit_reached?: boolean;
	orchestrator_context_window_usage?: number;
	/**
	 * AIFC: BE-provided classification for why no content was created in the staging area.
	 * Used by the no-content-created screen to show a differentiated message and to route
	 * the experience event between fail/abort. The set of values is intentionally enumerated
	 * here so that adding/removing a classification is a typed, traceable change. Unknown
	 * values received at runtime are collapsed into the safe fail path on the FE.
	 */
	aifc_no_create_classification?: NoCreateClassification;
	/** Studio SC3 automation rule UUID after ScheduleRovoChatTool execution (success message). */
	ruleIdUuid?: string;
	/** Studio SC3 numeric rule id (when provided alongside {@link ruleIdUuid}). */
	ruleId?: number;
	/** Automation rule rRule after ScheduleRovoChatTool execution (success message). */
	rRule?: string;
	key?: string;
};

export type Appendix = {
	type: 'requestForm' | 'helpDesk';
	content: string;
	appendix_sources?: Sources;
};

export type Appendices = Array<Appendix>;

type InternalTool = {
	name: string;
	/** true if this tool is an action */
	is_consequential: boolean;
	children: InternalTool[] | null;
};

/**
 * An array of arrays of minion/skill outputs
 */
export type MinionOutputs =
	| null
	| {
			minion_id: string;
			minion_input: string;
			minion_output: string;
			minion_execution_duration: number;
			minion_extra_info: string | null;
			/** is this an explicit skill/minion invocation */
			directly_invoked: boolean;
			/** skill slash command/slug */
			skill_alias: string | null;
			/** if this is a stratus skill or something native like search */
			skill_type: 'NATIVE' | 'STRATUS';
			/** all of the tools and actions triggered */
			internal_tools: InternalTool[];
	  }[][];

export type AgentAuthor = {
	id: string;
	versionId?: string;
	versionNumber?: number;
	versionType?: string;
};

/**
 * Start and end indices of insertable content substrings in FINAL_RESPONSE
 */
export type InsertableSection = {
	start: number;
	end: number;
};

export type Message = {
	role: string;
	content: string | DocNode;
	id: string;
	time_created: string;
	user_ari: string;
	plugin_invocations: PluginInvocationMessage[];
	sources?: Sources;
	message_metadata?: Metadata;
	author?: {
		actor_type: string;
		ari: string | null;
		id: string;
		name: string;
		/**
		 * @deprecated Use `external_config_reference` on the `Agent` object instead.
		 * `named_id` is being phased out. For OOTB avatar lookup, use
		 * `agent.external_config_reference` as `agentNamedId` on `AgentAvatar`.
		 */
		named_id?: string;
		/** Canonical agent identifier for OOTB avatar lookup (replaces named_id). */
		external_config_reference?: string;
		/** Forge/A2A agent identity account id for avatar resolution. */
		identity_account_id?: string;
		/** Creator type used to determine if this is a Forge agent. */
		creator_type?: string;
		/** Forge agent icon URL (used when rovo_agent_support_a2a_avatar is on). */
		icon?: string;
		versionId?: string;
		versionNumber?: number;
		versionType?: string;
	};
	agent_author?: AgentAuthor;
	author_id?: string;
	conversation_channel_id?: string;
	appendices?: Appendices;
	actions?: MessageAction[];
	async_status?: 'IN_PROGRESS' | 'SUCCESSFUL' | 'FAILED' | 'CANCELLED';
	content_mime_type?: 'text/markdown' | 'text/adf';
	experience_id?: string;
	cloud_id?: string;
	product_activation_id?: string;
	jira_activation_id?: string;
	user_org_id?: string;
	audio?: null;
	conversation_outcome?: null;
	orchestration_graphs?: null | unknown[];
	minion_outputs?: MinionOutputs;
	scenario?: {
		id: string;
		name: string;
		actions: string[];
	} | null;
	data_parts?: MessageDataPart[];
	insertable_sections?: InsertableSection[];
};

export type MessageDataPart = {
	data: unknown;
	metadata?: {
		mimeType?: string;
	};
	kind?: string;
};

export type StreamActionError = {
	type: string;
	message: {
		key: string;
		invocationId: string;
		actionId: string | null;
		data: { message: string };
		status: string;
	};
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	metadata?: any;
	millisOffset?: number;
};

export type AgentPlugins = {
	'Activity-Plugin': object;
	'Arithmetic-Plugin': object;
	'Content-Read-Plugin': object;
	'Jira-Issue-Create-Plugin': object;
	'Jira-JQL-Plugin': object;
	'Jira-Software-Plugin': object;
	'Next-Best-Task-Plugin': object;
	'Open-Toolchain-Plugin': object;
	'Profile-Plugin': object;
	'Search-QA-Plugin': {
		space_filter?: string[];
	};
	'Unified-Help-Plugin': object;
};

/* TODO: This will evolve as we enrich support for plugins, for the moment
we simply expect to make a direct replacement for `config.space_filters`
with `config.available_plugins['Your-Plugin'].space_filters`. Right now we only support
configuring one plugin, which is the Search-QA-Plugin, which can take the
space_filters configuration, per what our AgentForm expects

Related BE PR: https://bitbucket.org/atlassian/assistance-service/pull-requests/461/aim-367-allow-agents-to-use-plugin-config
*/
export type AgentAvailablePlugins = {
	[k in keyof AgentPlugins]?: AgentPlugins[k];
};

export type AgentKnowledgeSourceBase<T extends string, U = undefined> = {
	enabled: boolean;
	source: T;
	configuration: U;
};

export type ConfluenceKnowledgeSource = AgentKnowledgeSourceBase<
	'confluence',
	{
		space_filter: string[];
		parent_filter: string[];
	}
>;

export type JiraKnowledgeSource = AgentKnowledgeSourceBase<
	'jira',
	{
		project_filter: string[];
	}
>;

export type JsmKnowledgeSource = AgentKnowledgeSourceBase<
	'jsm',
	{
		project_filter: string[];
	}
>;

export type AtlasKnowledgeSource = AgentKnowledgeSourceBase<'atlas'>;
export type GoogleDriveKnowledgeSource = AgentKnowledgeSourceBase<'google_drive'>;
export type MicrosoftSharepointKnowledgeSource = AgentKnowledgeSourceBase<'microsoft_sharepoint'>;
export type SlackKnowledgeSource = AgentKnowledgeSourceBase<'slack'>;
export type LoomKnowledgeSource = AgentKnowledgeSourceBase<'loom'>;
export type OtherKnowledgeSource = AgentKnowledgeSourceBase<string>;

export type AgentKnowledgeSource =
	| ConfluenceKnowledgeSource
	| JiraKnowledgeSource
	| JsmKnowledgeSource
	| AtlasKnowledgeSource
	| GoogleDriveKnowledgeSource
	| MicrosoftSharepointKnowledgeSource
	| SlackKnowledgeSource
	| LoomKnowledgeSource
	| OtherKnowledgeSource;

export type AgentKnowledgeConfiguration = {
	// Known sources
	source:
		| 'confluence'
		| 'jira'
		| 'jsm'
		| 'atlas'
		| 'microsoft_sharepoint'
		| 'google_drive'
		| 'slack'
		| 'loom'
		| string;
	iconUrl: string;
	friendlyName: string;
	isUserOAuthed: boolean;
	outboundAuthUrl: string;
	connectorCreatedAt: string | null;
	connectedAt: string | null;
	resourceType: string | null;
	providerId: string | null;
	datasourceId: string | null;
	connectorSyncType: string | null;
};

export type MappedAgentKnowledgeConfiguration = AgentKnowledgeConfiguration & {
	checkboxFieldName: string;
	actionSubjectId: string;
	checkboxLabel: string;
};

export type AgentKnowledgeConfigurationResponse = {
	knowledgeSources: AgentKnowledgeConfiguration[];
};

export type AgentScenarioKnowledgeSource = {
	enabled?: boolean;
	source: string;
	filters?: {
		parent_filter?: string[];
		space_filter?: string[];
		project_filter?: string[];
	};
};

export type AgentScenarioAction = {
	id: string;
	name?: string;
	displayName?: string;
	description?: string;
	tags?: string[];
	iconUrl?: string;
};

export type AgentScenario = {
	id: string;
	name: string;
	invocationDescription?: string;
	instructions?: string;
	knowledgeSources?: {
		enabled?: boolean;
		sources?: AgentScenarioKnowledgeSource[];
	};
	actions?: AgentScenarioAction[];
};

export type Agent = {
	id: string;
	identity_account_id?: string | null;
	named_id: string;
	name: string;
	description: string | null;
	system_prompt_template?: string | null;
	behaviour?: string | null;
	creator_type: 'SYSTEM' | 'CUSTOMER' | 'THIRD_PARTY' | 'FORGE' | 'OOTB' | 'REMOTE_A2A';
	creator?: string | null;
	available_plugins: AgentAvailablePlugins;
	visibility?: 'PUBLIC' | 'PRIVATE' | null;
	is_default: boolean;
	external_files?: ExternalFile[] | null;
	actor_type: 'AGENT';
	creator_cloud_id?: string | null;
	follow_up_prompt_template?: string | null;
	plugin_routing_type?: 'DEFAULT' | 'SKIP' | null;
	user_defined_conversation_starters: string[] | null;
	favourite: boolean;
	deactivated: boolean;
	deactivatedAt?: string;
	knowledge_sources?: {
		enabled: boolean;
		sources: AgentKnowledgeSource[];
	} | null;
	action_config?: AgentAction[] | null;
	external_config_reference?: string;
	icon?: string | null;
	authoring_team?: {
		id: string;
		profile_url: string;
		display_name: string;
		avatar_url?: string;
	} | null;
	is_verified?: boolean | null;
	agentAri?: string | null;
	execution_config?: {
		execution_tier?: 'TIER_0' | 'TIER_1' | 'TIER_2' | null;
	} | null;
};

export type AgentDetails = Agent & {
	favourite_count: number;
};

export type ConversationChannelDataMessage = {
	type: 'CONVERSATION_CHANNEL_DATA';
	assistant_message_id: string;
} & Pick<ConversationChannel, 'name' | 'time_created' | 'last_message_timestamp' | 'scopes'>;

/**
 * Live update of the conversation's scope list, emitted by the BE once
 * scope extraction completes for the current turn. Mirrors the existing
 * `ConversationChannelScope` shape so consumers can reuse the same type
 * already returned by `GET /conversations`. The BE omits this event when
 * the resolved scope set is unchanged (no-op guard).
 *
 * Ordering: arrives after `CONVERSATION_CHANNEL_DATA` and before
 * `FINAL_RESPONSE` on the SSE stream.
 */
export type ScopeDeterminedMessage = {
	type: 'SCOPE_DETERMINED';
	conversation_id: string;
	assistant_message_id?: string;
	scopes: ConversationChannelScope[];
	sequence_id?: string;
};

export type ConversationStarterResponse = {
	conversation_starters: { text: string }[];
};

export type DynamicConversationStarterRequestNew =
	AiFeatureRequestEnvelopeRovoConvoStarterRecommendationRequestNew;

export type DynamicConversationStarterResponseNew =
	| AIFeatureResponseEnvelopeRovoConvoStarterRecommendationResponseNew
	| undefined;

export type FetchStreamReturn<T, isSSEFormatted extends boolean = false> = {
	responseData: {
		traceId?: string;
	};
	generator: isSSEFormatted extends true
		? AsyncGenerator<ParsedSSEMessage<T>> // SSE: always ParsedSSEMessage<T>
		: AsyncGenerator<T>; // Non-SSE: clean type, no check needed
};
